/**
 * WhatsAppCampaignsPage
 *
 * Main page for composing and sending WhatsApp Business campaigns.
 * Mirrors the layout of NotificationsPage: configuration on the left,
 * preview + summary on the right.
 *
 * Differences vs. notifications:
 *  - Pre-approved templates instead of free-text title/body
 *  - Variable editor for {{1}}, {{2}}, … placeholders
 *  - Cost estimator (Meta charges per conversation)
 *  - Realistic WhatsApp chat preview
 *  - AI template generator
 *  - A/B testing
 *  - Real scheduler with AI-suggested time
 *  - Quality rating widget
 */

import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  BarChart3,
  CheckCircle,
  FileText,
  MessageCircle,
  RotateCcw,
} from "lucide-react";
import { Button } from "../../../shared/components/ui/Button";
import { SegmentSelector } from "../components/SegmentSelector";
import { TemplateSelector } from "../components/TemplateSelector";
import WhatsAppSubNav from "../components/WhatsAppSubNav";
import AccountSelector from "../components/AccountSelector";
import { whatsappAccountsApi } from "../api";
import type { WhatsAppAccount } from "../types";
import { VariableEditor } from "../components/VariableEditor";
import { WhatsAppPreview } from "../components/WhatsAppPreview";
import { CostEstimator } from "../components/CostEstimator";
import { SendSummary } from "../components/SendSummary";
import { ConfirmModal } from "../components/ConfirmModal";
import { ScheduleModal } from "../components/ScheduleModal";
import { AITemplateGenerator } from "../components/AITemplateGenerator";
import { ABTestingPanel } from "../components/ABTestingPanel";
import { QualityRating } from "../components/QualityRating";
import { useWhatsAppCampaigns } from "../hooks/useWhatsAppCampaigns";
import { DYNAMIC_FIELDS } from "../components/PersonalizationToggle";

/** Example values for each dynamic field — used for preview rendering */
const DYNAMIC_EXAMPLES = DYNAMIC_FIELDS.reduce<Record<string, string>>(
  (acc, f) => {
    acc[f.key] = f.example;
    return acc;
  },
  {},
);

const MOCK_BRAND_ID = "brand-demo";

export function WhatsAppCampaignsPage() {
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [showAIGenerator, setShowAIGenerator] = useState(false);

  const {
    segments,
    segmentsLoading,
    templates,
    templatesLoading,
    selectedSegment,
    selectedTemplate,
    addTemplate,

    form,
    updateForm,
    selectTemplate,
    setBodyVariable,
    dynamicFields,
    setDynamicField,
    resetForm,
    isValid,
    estimatedCost,

    abEnabled,
    setAbEnabled,
    variantBTemplate,
    setVariantBTemplateId,
    splitPct,
    setSplitPct,
    winnerCriterion,
    setWinnerCriterion,

    sendCampaign,
    scheduleCampaign,
    sending,
    error,
    success,
  } = useWhatsAppCampaigns(MOCK_BRAND_ID);

  const handleSendClick = () => setShowConfirmModal(true);

  const handleConfirmSend = async () => {
    const ok = await sendCampaign();
    if (ok) setShowConfirmModal(false);
  };

  const handleSchedule = async (isoTimestamp: string) => {
    const ok = await scheduleCampaign(isoTimestamp);
    if (ok) setShowScheduleModal(false);
  };

  /* ─── Accounts state (lista completa para selector + count para subnav) ─── */
  const [accounts, setAccounts] = useState<WhatsAppAccount[]>([]);
  const [accountsLoading, setAccountsLoading] = useState(true);
  const [selectedAccountId, setSelectedAccountId] = useState<string | undefined>();
  useEffect(() => {
    (async () => {
      setAccountsLoading(true);
      const r = await whatsappAccountsApi.list();
      if (r.success) setAccounts(r.data);
      setAccountsLoading(false);
    })();
  }, []);
  const accountsCount = accounts.length;
  const selectedAccount = accounts.find((a) => a.id === selectedAccountId);
  const accountSelected = !!selectedAccount;
  const accountUsable =
    selectedAccount?.status === "connected" &&
    (selectedAccount.activationStatus === "active" ||
      selectedAccount.activationStatus === "warming_up");

  /* ─── Success state ─── */
  if (success) {
    return (
      <div className="min-h-screen p-6 md:p-8 animate-fade-in">
        <div className="mx-auto max-w-2xl text-center py-20">
          <div className="w-20 h-20 bg-emerald-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
            <CheckCircle size={40} className="text-emerald-600" />
          </div>
          <h2 className="text-3xl font-bold text-slate-900 mb-3">
            ¡Campaña Enviada!
          </h2>
          <p className="text-slate-500 text-lg mb-8 leading-relaxed">
            El envío de WhatsApp se está despachando a{" "}
            <span className="font-semibold text-emerald-600">
              {selectedSegment?.lastKnownCount.toLocaleString()}
            </span>{" "}
            usuarios del segmento{" "}
            <span className="font-semibold">"{selectedSegment?.name}"</span>.
          </p>
          <div className="flex gap-3 justify-center">
            <Button variant="primary" size="lg" onClick={resetForm}>
              <RotateCcw size={16} />
              Hacer otro envío
            </Button>
            <Link to="/whatsapp/analytics">
              <Button variant="secondary" size="lg">
                <BarChart3 size={16} />
                Ver analytics
              </Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-6 md:p-8 animate-fade-in">
      <div className="mx-auto max-w-7xl space-y-6">
        <WhatsAppSubNav active="campaigns" accountsCount={accountsCount} />
        {/* Header */}
        <div className="flex flex-col md:flex-row md:justify-between md:items-end gap-4 mb-2">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <h1 className="text-3xl font-bold text-slate-900 tracking-tight">
                Envíos de WhatsApp
              </h1>
            </div>
            <p className="text-slate-500 text-sm mt-1">
              Envíos puntuales por WhatsApp: elegí segmento, plantilla y desde qué
              cuenta sale. Para automatizaciones recurrentes (carrito abandonado,
              cumpleaños, reactivación) usá el módulo de <strong>Campañas</strong>{" "}
              ← menú izquierdo, que las corre automáticamente por vos.
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="secondary" onClick={resetForm} disabled={sending}>
              <RotateCcw size={16} />
              Limpiar
            </Button>
          </div>
        </div>

        {/* Error banner */}
        {error && (
          <div className="flex items-center gap-3 p-4 bg-red-50 border border-red-100 rounded-xl text-red-700 animate-fade-in">
            <MessageCircle size={18} />
            <span className="text-sm font-medium">{error}</span>
          </div>
        )}

        {/* Quality rating strip — compact and global */}
        <QualityRating
          tier="green"
          score={92}
          dailyLimit={10000}
          usedToday={3540}
        />

        {/* Two-column layout */}
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
          {/* Left: config */}
          <div className="lg:col-span-3 space-y-6">
            <AccountSelector
              accounts={accounts}
              selectedId={selectedAccountId}
              onSelect={setSelectedAccountId}
              loading={accountsLoading}
            />

            {/* Warm-up rate limit warning si la cuenta seleccionada está en warmup */}
            {selectedAccount?.activationStatus === "warming_up" &&
              selectedAccount.warmupPlan && (
                <div className="bg-orange-50 border border-orange-200 rounded-xl p-3 flex items-start gap-2.5">
                  <div className="text-orange-600 mt-0.5 flex-shrink-0">
                    🚀
                  </div>
                  <div className="text-xs text-orange-800">
                    <span className="font-semibold">
                      {selectedAccount.displayName} está en warm-up
                    </span>{" "}
                    (día{" "}
                    {selectedAccount.warmupPlan.currentDay} de{" "}
                    {selectedAccount.warmupPlan.totalDays}). Tu envío no puede
                    superar{" "}
                    <span className="font-bold">
                      {selectedAccount.warmupPlan.dailyLimit -
                        selectedAccount.warmupPlan.usedToday}{" "}
                      mensajes
                    </span>{" "}
                    en lo que queda del día. Si necesitás más, repartí entre
                    cuentas o programá para mañana.
                  </div>
                </div>
              )}

            <SegmentSelector
              segments={segments}
              selectedSegment={selectedSegment}
              loading={segmentsLoading}
              onSelect={(id) => updateForm({ segmentId: id })}
            />

            <TemplateSelector
              templates={templates}
              selectedTemplate={selectedTemplate}
              loading={templatesLoading}
              onSelect={selectTemplate}
              onOpenAIGenerator={() => setShowAIGenerator(true)}
            />

            {selectedTemplate && (
              <VariableEditor
                template={selectedTemplate}
                bodyVariables={form.bodyVariables}
                dynamicFields={dynamicFields}
                headerVariable={form.headerVariable}
                headerMediaUrl={form.headerMediaUrl}
                onBodyVariableChange={setBodyVariable}
                onDynamicFieldChange={setDynamicField}
                onHeaderVariableChange={(value) =>
                  updateForm({ headerVariable: value })
                }
                onHeaderMediaChange={(dataUrl) =>
                  updateForm({ headerMediaUrl: dataUrl || undefined })
                }
              />
            )}

            <ABTestingPanel
              enabled={abEnabled}
              onToggle={setAbEnabled}
              templates={templates}
              templatesLoading={templatesLoading}
              variantATemplate={selectedTemplate}
              variantBTemplate={variantBTemplate}
              onSelectVariantB={setVariantBTemplateId}
              splitPct={splitPct}
              onSplitChange={setSplitPct}
              criterion={winnerCriterion}
              onCriterionChange={setWinnerCriterion}
            />
          </div>

          {/* Right: preview + summary */}
          <div className="lg:col-span-2 space-y-6">
            <WhatsAppPreview
              template={selectedTemplate}
              bodyVariables={form.bodyVariables}
              dynamicFields={dynamicFields}
              dynamicExamples={DYNAMIC_EXAMPLES}
              headerVariable={form.headerVariable}
              headerMediaUrl={form.headerMediaUrl}
            />

            {/* Estimación de costo — visible antes del botón Enviar */}
            <CostEstimator
              segment={selectedSegment}
              template={selectedTemplate}
              estimatedCost={estimatedCost}
            />
          </div>
        </div>

        {/* Full-width footer bar with send actions — sticky al fondo del viewport */}
        <div className="sticky bottom-0 z-10 -mx-6 md:-mx-8 px-6 md:px-8 bg-white/95 backdrop-blur-sm border-t border-slate-100 shadow-[0_-6px_20px_rgba(0,0,0,0.06)]">
          <SendSummary
            segment={selectedSegment}
            template={selectedTemplate}
            isValid={isValid && accountSelected && accountUsable}
            sending={sending}
            onSend={handleSendClick}
            onSchedule={() => setShowScheduleModal(true)}
            account={selectedAccount}
          />
        </div>
      </div>

      {/* Modals */}
      {showConfirmModal && selectedSegment && selectedTemplate && (
        <ConfirmModal
          recipientCount={selectedSegment.lastKnownCount}
          segmentName={selectedSegment.name}
          templateName={selectedTemplate.name}
          estimatedCostUSD={estimatedCost}
          onConfirm={handleConfirmSend}
          onCancel={() => setShowConfirmModal(false)}
          sending={sending}
        />
      )}

      <ScheduleModal
        open={showScheduleModal}
        recipientCount={selectedSegment?.lastKnownCount || 0}
        sending={sending}
        onSchedule={handleSchedule}
        onCancel={() => setShowScheduleModal(false)}
      />

      <AITemplateGenerator
        open={showAIGenerator}
        onClose={() => setShowAIGenerator(false)}
        onAccept={(template) => {
          addTemplate(template);
          selectTemplate(template.id);
        }}
      />
    </div>
  );
}

export default WhatsAppCampaignsPage;
